import { useMemo, useState } from "react";
import { Select } from "@headlessui/react";
import { ref, remove, update } from "firebase/database";
import RoomForm from "../components/RoomForm";
import ConfirmDialog from "../components/ConfirmDialog";
import { useFirebase } from "../FbContex";
import { db } from "../firebase";
import { IRoom, WithId } from "../@types/room";
import { allRooms, nodesOfRoom, swapOrder } from "../lib/selectors";
import {
  BTN_PRIMARY,
  SELECT_CLASS,
  TD,
  TD_HEAD,
  TH,
} from "../components/tableStyles";
import { AdminSection, OrderCell, ToggleCell } from "../components/table/Cells";

export default function Rooms() {
  const { rooms, devices } = useFirebase();
  const [room, setRoom] = useState<IRoom | null>(null);
  const [target, setTarget] = useState<WithId<IRoom> | null>(null);
  const [moveTo, setMoveTo] = useState("");
  const [busy, setBusy] = useState(false);

  const list = useMemo(() => allRooms(rooms), [rooms]);
  const owned = target ? nodesOfRoom(devices, target.id) : [];
  const others = list.filter((r) => r.id !== target?.id);
  const blocked = owned.length > 0 && others.length === 0;

  const toggle = (r: WithId<IRoom>) =>
    update(ref(db, `rooms/${r.id}`), { disabled: !r.disabled });

  const confirmDelete = () => {
    if (!target) return;
    setBusy(true);

    // One atomic multi-path write: the room goes and its devices are reassigned
    // in the same transaction, so there is never a half state with orphans.
    const updates: { [path: string]: any } = { [`rooms/${target.id}`]: null };
    owned.forEach((n) => (updates[`devices/${n.id}/roomId`] = moveTo));

    const op = owned.length
      ? update(ref(db), updates)
      : remove(ref(db, `rooms/${target.id}`));

    op.catch((err) => alert(err.message)).finally(() => {
      setBusy(false);
      setTarget(null);
      setMoveTo("");
    });
  };

  return (
    <>
      <RoomForm room={room} close={() => setRoom(null)} />
      <ConfirmDialog
        open={!!target}
        title={`Delete ${target?.name ?? "room"}?`}
        busy={busy}
        confirmDisabled={blocked || (owned.length > 0 && !moveTo)}
        onConfirm={confirmDelete}
        onCancel={() => {
          setTarget(null);
          setMoveTo("");
        }}
      >
        {owned.length === 0 ? (
          <p>This room has no devices. It will be removed permanently.</p>
        ) : (
          <>
            <p>
              {owned.length} device{owned.length > 1 ? "s" : ""} belong to this
              room:
            </p>
            <ul className="list-disc list-inside opacity-70">
              {owned.map((n) => (
                <li key={n.id}>{n.title}</li>
              ))}
            </ul>
            {blocked ? (
              <p className="text-red-400">
                There is no other room to move them to. Create another room
                first, or delete these devices.
              </p>
            ) : (
              <div>
                <div className="mb-1 opacity-60">Move devices to</div>
                <Select
                  value={moveTo}
                  onChange={(e) => setMoveTo(e.target.value)}
                  className={SELECT_CLASS}
                >
                  <option value="">--select a room--</option>
                  {others.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </Select>
              </div>
            )}
          </>
        )}
      </ConfirmDialog>

      <AdminSection
        title="Rooms"
        actions={
          <button
            className={BTN_PRIMARY}
            type="button"
            onClick={() => setRoom({} as IRoom)}
          >
            Add new
          </button>
        }
      >
        <thead>
          <tr>
            <th className={TH}>name</th>
            <th className={TH}>slug</th>
            <th className={TH}>devices</th>
            <th className={TH}>enabled</th>
            <th className={TH}>order</th>
            <th className={TH}>action</th>
          </tr>
        </thead>
        <tbody>
          {list.length === 0 && (
            <tr>
              <td className={`${TD} opacity-50`} colSpan={6}>
                No rooms yet.
              </td>
            </tr>
          )}
          {list.map((r, i) => (
            <tr key={r.id} className={r.disabled ? "opacity-50" : ""}>
              <th className={TD_HEAD}>
                {r.icon && <i className={`fas fa-${r.icon} mr-2 opacity-60`} />}
                {r.name}
              </th>
              <td className={TD}>{r.slug}</td>
              <td className={TD}>{nodesOfRoom(devices, r.id).length}</td>
              <ToggleCell checked={!r.disabled} onChange={() => toggle(r)} />
              <OrderCell
                index={i}
                count={list.length}
                onMove={(dir) => swapOrder("rooms", list, i, dir)}
              />
              <td className={TD}>
                <i
                  onClick={() => setRoom({ ...r })}
                  className="fas fa-pencil text-emerald-500 mr-4 cursor-pointer hover:opacity-75"
                />
                <i
                  onClick={() => setTarget(r)}
                  className="fas fa-trash text-red-500 cursor-pointer hover:opacity-75"
                />
              </td>
            </tr>
          ))}
        </tbody>
      </AdminSection>
    </>
  );
}
